import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Loader2, Link2, AlertCircle } from 'lucide-react'
import { useTreatments, useTreatmentCategories, useCreateVariation } from '@/hooks/useTreatments'
import type { UnmatchedItem, Treatment } from '@/lib/api-client'

interface ManualMappingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  unmatchedItem: UnmatchedItem | null
  onSuccess?: () => void
}

export function ManualMappingDialog({
  open,
  onOpenChange,
  unmatchedItem,
  onSuccess,
}: ManualMappingDialogProps) {
  const [selectedTreatmentId, setSelectedTreatmentId] = React.useState<string>('')
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string>('all')

  // Fetch all treatments (no pagination, get all)
  const { data: treatmentsData, isPending: treatmentsLoading } = useTreatments({
    pageSize: 200, // Get all
    sortBy: 'name',
    sortOrder: 'asc',
  })

  // Fetch categories for filtering
  const { data: categoriesData } = useTreatmentCategories()

  // Create variation mutation
  const createVariation = useCreateVariation()

  // Filter treatments by category
  const treatments = React.useMemo(() => {
    if (!treatmentsData?.data) return []
    if (!selectedCategoryId || selectedCategoryId === 'all') return treatmentsData.data
    return treatmentsData.data.filter(
      (t) => t.categoryId?.toString() === selectedCategoryId
    )
  }, [treatmentsData?.data, selectedCategoryId])

  const categories = categoriesData?.data ?? []

  // Reset selection when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setSelectedTreatmentId('')
      setSelectedCategoryId('all')
    }
  }, [open])

  const handleSubmit = async () => {
    if (!unmatchedItem || !selectedTreatmentId) return

    await createVariation.mutateAsync({
      variationName: unmatchedItem.name,
      treatmentId: parseInt(selectedTreatmentId),
      matchType: 'manual',
      confidence: 1.0,
    })

    onOpenChange(false)
    onSuccess?.()
  }

  const selectedTreatment = treatments.find(
    (t) => t.id.toString() === selectedTreatmentId
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Map to Master Treatment
          </DialogTitle>
          <DialogDescription>
            Link this unmatched item to an existing master treatment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Unmatched Item Info */}
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
            <Label className="text-xs text-muted-foreground">Unmatched Item</Label>
            <p className="mt-1 font-mono text-sm font-medium">
              {unmatchedItem?.name}
            </p>
            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{unmatchedItem?.count.toLocaleString()} occurrences</span>
              <span>•</span>
              <span>RM {unmatchedItem?.revenue.toLocaleString()}</span>
            </div>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <Label>Filter by Category (Optional)</Label>
            <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.name} ({cat.treatmentCount})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Treatment Selection */}
          <div className="space-y-2">
            <Label>Select Master Treatment *</Label>
            <Select
              value={selectedTreatmentId}
              onValueChange={setSelectedTreatmentId}
              disabled={treatmentsLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    treatmentsLoading ? 'Loading...' : 'Select a treatment'
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {treatments.map((treatment) => (
                  <SelectItem key={treatment.id} value={treatment.id.toString()}>
                    <div className="flex items-center gap-2">
                      <span>{treatment.name}</span>
                      {treatment.categoryName && (
                        <Badge variant="outline" className="text-xs">
                          {treatment.categoryName}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {treatments.length === 0 && !treatmentsLoading && (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    No treatments found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Treatment Preview */}
          {selectedTreatment && (
            <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4">
              <Label className="text-xs text-muted-foreground">
                Will be mapped to
              </Label>
              <p className="mt-1 font-medium text-green-600 dark:text-green-400">
                {selectedTreatment.name}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {selectedTreatment.categoryName && (
                  <Badge variant="secondary">{selectedTreatment.categoryName}</Badge>
                )}
                <Badge variant="outline">{selectedTreatment.type}</Badge>
                <span className="text-xs text-muted-foreground">
                  {selectedTreatment.variationCount} variations
                </span>
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              This will update all {unmatchedItem?.count.toLocaleString()} invoice
              items with this name to use the selected master treatment.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedTreatmentId || createVariation.isPending}
          >
            {createVariation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Map Treatment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
